import { useEffect, useMemo, useState } from "react"

import { ENV } from "@/config/env"

export type MaintenanceSeverity = "info" | "warning" | "critical"

type MaintenanceConfig = {
  /** Whether maintenance mode is active */
  isActive: boolean
  /** Severity level affects styling and urgency */
  severity: MaintenanceSeverity
  /** Estimated duration in minutes */
  estimatedMinutes: number
  /** When maintenance started (for countdown) */
  startedAt: Date | null
  /** Custom message override */
  customMessage: string | null
  /** Status page URL */
  statusPageUrl: string | null
  /** Minutes remaining (calculated) */
  minutesRemaining: number | null
  /** Progress percentage (0-100) */
  progressPercent: number | null
  /** Default display state (opened or closed) */
  defaultState: "opened" | "closed"
  /** Whether to destroy component on dismiss instead of collapsing */
  destroyOnDismiss: boolean
}

/**
 * Hook to manage maintenance mode state from environment variables.
 *
 * Displays a maintenance banner when VITE_MAINTENANCE_MODE=true.
 * Provides countdown timer, progress bar, and warm user messaging.
 *
 * Environment Variables:
 * - VITE_MAINTENANCE_MODE: Enable/disable banner ("true" | "false")
 * - VITE_MAINTENANCE_SEVERITY: Visual style ("info" | "warning" | "critical")
 * - VITE_MAINTENANCE_MINUTES: Estimated duration in minutes
 * - VITE_MAINTENANCE_STARTED_AT: ISO timestamp for countdown timer
 * - VITE_MAINTENANCE_MESSAGE: Custom message override
 * - VITE_MAINTENANCE_DEFAULT_STATE: Initial display ("opened" | "closed")
 * - VITE_MAINTENANCE_DESTROY_ON_DISMISS: Remove completely when dismissed ("true" | "false")
 * - VITE_STATUS_PAGE_URL: Link to status page
 *
 * @example
 * ```tsx
 * const config = useMaintenanceMode()
 * if (config.isActive) {
 *   // Show maintenance banner
 * }
 * ```
 */
export function useMaintenanceMode() {
  const [now, setNow] = useState(() => new Date())

  // Update time every minute for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date())
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [])

  const config = useMemo<MaintenanceConfig>(() => {
    const isActive = ENV.MAINTENANCE_MODE === "true"
    const severity = ENV.MAINTENANCE_SEVERITY as MaintenanceSeverity
    const estimatedMinutes = Number.parseInt(ENV.MAINTENANCE_MINUTES, 10)
    const startedAtStr = ENV.MAINTENANCE_STARTED_AT
    const customMessage = ENV.MAINTENANCE_MESSAGE || null
    const statusPageUrl = ENV.STATUS_PAGE_URL || null
    const defaultState = ENV.MAINTENANCE_DEFAULT_STATE as "opened" | "closed"
    const destroyOnDismiss = ENV.MAINTENANCE_DESTROY_ON_DISMISS === "true"

    let startedAt: Date | null = null
    let minutesRemaining: number | null = null
    let progressPercent: number | null = null

    if (startedAtStr) {
      startedAt = new Date(startedAtStr)
      const elapsedMs = now.getTime() - startedAt.getTime()
      const elapsedMinutes = Math.floor(elapsedMs / 60000)
      minutesRemaining = Math.max(0, estimatedMinutes - elapsedMinutes)
      progressPercent = Math.min(100, Math.round((elapsedMinutes / estimatedMinutes) * 100))
    }

    return {
      isActive,
      severity,
      estimatedMinutes,
      startedAt,
      customMessage,
      statusPageUrl,
      minutesRemaining,
      progressPercent,
      defaultState,
      destroyOnDismiss
    }
  }, [now])

  return config
}
